import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';

interface BaseModalProps {
  show: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string;
  size?: 'small' | 'medium' | 'large' | 'xlarge';
  className?: string;
  children: React.ReactNode;
  showHeader?: boolean;
  headerIcon?: React.ReactNode;
  headerGradient?: boolean;
}

const BaseModal: React.FC<BaseModalProps> = ({
  show,
  onClose,
  title,
  subtitle,
  size = 'medium',
  className = '',
  children,
  showHeader = true,
  headerIcon,
  headerGradient = true
}) => {
  // Handle ESC key press
  useEffect(() => {
    if (!show) return;
    
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleEsc);
    
    // Add body overflow hidden when modal is open
    document.body.style.overflow = 'hidden';
    
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = 'unset';
    };
  }, [show, onClose]);

  if (!show) return null;

  const sizeClasses = {
    small: 'max-w-md',
    medium: 'max-w-2xl',
    large: 'max-w-4xl',
    xlarge: 'max-w-6xl'
  };

  const modalContent = (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        backdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
        zIndex: 9999
      }}
      onClick={onClose}
    >
      <div 
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          width: '100%',
          maxWidth: sizeClasses[size] === 'max-w-md' ? '448px' : 
                   sizeClasses[size] === 'max-w-2xl' ? '672px' :
                   sizeClasses[size] === 'max-w-4xl' ? '896px' : '1152px',
          maxHeight: '90vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
        className={className}
        onClick={(e) => e.stopPropagation()}
      >
        {showHeader && (
          <div 
            style={{
              padding: '16px 24px',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: headerGradient ? 'linear-gradient(to right, #3b82f6, #9333ea)' : 'transparent'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              {headerIcon && (
                <div style={{
                  padding: '8px',
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  borderRadius: '8px'
                }}>
                  {headerIcon}
                </div>
              )}
              <div>
                {title && (
                  <h2 style={{
                    fontSize: '20px',
                    fontWeight: 600,
                    color: headerGradient ? 'white' : '#111827'
                  }}>
                    {title}
                  </h2>
                )}
                {subtitle && (
                  <p style={{
                    fontSize: '14px',
                    marginTop: '4px',
                    color: headerGradient ? 'rgba(255, 255, 255, 0.9)' : '#6b7280'
                  }}>
                    {subtitle}
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              style={{
                padding: '8px',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: 'transparent',
                color: headerGradient ? 'white' : '#6b7280',
                cursor: 'pointer',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = headerGradient 
                  ? 'rgba(255, 255, 255, 0.2)' 
                  : '#f3f4f6';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <svg style={{ width: '20px', height: '20px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}
        
        <div style={{ flex: 1, overflow: 'auto' }}>
          {children}
        </div>
      </div>
    </div>
  );

  // Ensure modal root exists
  let modalRoot = document.getElementById('modal-root');
  if (!modalRoot) {
    modalRoot = document.createElement('div');
    modalRoot.id = 'modal-root';
    document.body.appendChild(modalRoot);
  }
  
  return createPortal(modalContent, modalRoot);
};

export default BaseModal;